import keys from '@debbie/common/dist/cache/keys.js';
import { CaseStatus } from '@debbie/common/dist/cases/index.js';
import { CaseVoucherSource } from '@debbie/common/dist/economy/case-vouchers/index.js';
import helpers from '@debbie/common/dist/economy/helpers/index.js';
import * as paymentPlanHelpers from '@debbie/common/dist/economy/payment-plans/index.js';
import { VoucherCategory } from '@debbie/common/dist/economy/vouchers/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { paymentPlans as paymentPlansApi } from '@debbie/cortex/dist/api/index.js';
import { useCaseConditions } from '@debbie/cortex/dist/case-conditions/index.js';
import { caseVouchersEntity } from '@debbie/cortex/dist/case-vouchers/index.js';
import { useCase } from '@debbie/cortex/dist/cases/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { useEconomyPolicy } from '@debbie/cortex/dist/economy-policy/index.js';
import { useFixedDistributionsByCaseId } from '@debbie/cortex/dist/fixed-distributions/index.js';
import { useGetBalance, useInterestCalulation, } from '@debbie/cortex/dist/heavy-calculations/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { interactionsEntity } from '@debbie/cortex/dist/interactions/index.js';
import { paymentPlansEntity, usePaymentPlansByCaseId, } from '@debbie/cortex/dist/paymentPlans/index.js';
import q from '@debbie/cortex/dist/query.js';
import { useTenant } from '@debbie/cortex/dist/tenant/index.js';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import AlertCmp from '../Alert.js';
import Button from '../Button.js';
import { ArrowRight } from '../Icons/index.js';
import { Alert, Checkbox, Errors, ExclusiveContent, PlaceholderLoader, RadioSelect, } from '../index.js';
import Money from '../Money.js';
import PaymentPlanEntries from './components/PaymentPlanEntries.js';
import PaymentPlanSuggestions from './components/PaymentPlanSuggestions.js';
import PaymentPlan from './PaymentPlan.js';
import PaymentPlanEditor from './PaymentPlanEditor.js';
const PaymentPlanCreator = (props) => {
    const lang = useSelector((s) => s.lang);
    const tenant = useTenant();
    const singleCaseLoader = useCase(props.caseId);
    const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null;
    const paymentPlansLoadable = usePaymentPlansByCaseId(props.caseId);
    const paymentPlans = h.hasData(paymentPlansLoadable)
        ? paymentPlansLoadable.data
        : null;
    const paymentPlansIsLoading = h.isLoading(paymentPlansLoadable);
    const documentsLoader = useDocumentsByReference(props.caseId);
    const documents = h.hasData(documentsLoader) ? documentsLoader.data : null;
    const creditorLoadable = useCreditor(singleCase?.creditorId);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const economyPolicy = useEconomyPolicy(singleCase);
    const conditionsLoader = useCaseConditions(props.caseId);
    const conditions = h.hasData(conditionsLoader) ? conditionsLoader.data : null;
    const [plan, setPlan] = useState([]);
    const [config, setConfig] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [activationTrigger, setActivationTrigger] = useState(true);
    const [customPlanEditor, setCustomPlanEditor] = useState(true);
    const paymentPlan = paymentPlans && paymentPlanHelpers.getCurrentPaymentPlan(paymentPlans);
    const selectPlan = (id, plan, config) => {
        setSelectedPlan(id);
        setPlan(plan);
        setConfig(config);
    };
    const createPaymentPlanMutation = useMutation(async () => {
        await paymentPlansApi.create(props.caseId, {
            activationTrigger,
            selectedPaymentPlanId: selectedPlan && selectedPlan !== 'CUSTOM' ? selectedPlan : undefined,
            plan: plan.map((entry) => ({
                amount: entry.amount,
                cancel: entry.cancel,
                deadline: entry.deadline,
                reminder: entry.reminder,
                date: entry.date,
                type: entry.type,
            })),
            config: config ?? null,
        });
        q.invalidate([
            ...keys.write.create(paymentPlansEntity),
            ...keys.write.create(caseVouchersEntity),
            ...keys.write.create(interactionsEntity),
        ]);
    }, {
        onError: (err) => {
            errorNotification(lang, err, 'create', lang.errorEntities.paymentPlan);
        },
    });
    const fixedDistributionsLoader = useFixedDistributionsByCaseId(props.caseId);
    const fixedDistributions = h.hasData(fixedDistributionsLoader)
        ? fixedDistributionsLoader.data
        : null;
    const today = useMemo(() => moment().format(), []);
    const vouchersTodayLoader = useInterestCalulation(helpers.vouchers.postProcessing(props.caseVouchers, {
        includeDeletedVouchers: false,
        includeExpiredVouchers: false,
        includeIrrecoverableVouchers: false,
    }), documents, economyPolicy, fixedDistributions, conditions, today, {});
    const vouchersToday = h.hasData(vouchersTodayLoader)
        ? vouchersTodayLoader.data
        : [];
    const baseCurrentInterest = helpers.overview.balanceCategory(props.caseVouchers, VoucherCategory.INTEREST);
    const calcedCurrentInterest = helpers.overview.balanceCategory(vouchersToday, VoucherCategory.INTEREST);
    const currentInterest = h.hasData(vouchersTodayLoader)
        ? calcedCurrentInterest
        : baseCurrentInterest;
    const paymentPlanConfig = paymentPlanHelpers.resolvePaymentPlansConfig(creditor?.paymentPlansConfig, tenant?.paymentPlansConfig);
    return (React.createElement(PaymentPlanCreatorStyled, null, paymentPlanConfig.disablePaymentPlans ? (React.createElement(Alert, { message: lang.paymentPlans.paymentPlansDisabled })) : (React.createElement(React.Fragment, null,
        h.isError(conditionsLoader) && (React.createElement(Alert, { message: lang.costCeiling.failedToLoad })),
        React.createElement("div", null, paymentPlansIsLoading ? (React.createElement(PlaceholderLoader, null)) : (!paymentPlan && (React.createElement("div", null,
            React.createElement("div", null,
                React.createElement(RadioSelect, { label: lang.paymentPlans.createCustomPlan, active: customPlanEditor, onClick: () => {
                        setCustomPlanEditor(true);
                        selectPlan(null, [], config);
                    } }),
                React.createElement(RadioSelect, { label: lang.paymentPlans.createStandardPlan, active: !customPlanEditor, onClick: () => {
                        setCustomPlanEditor(false);
                        selectPlan(null, [], config);
                    } }),
                React.createElement("br", null),
                React.createElement("br", null),
                customPlanEditor ? (React.createElement("div", null,
                    React.createElement(PaymentPlanEditor, { creditor: creditor, caseId: props.caseId, singleCase: singleCase, selectPlan: selectPlan, caseVouchers: props.caseVouchers, defaultFeeStructure: paymentPlanConfig.defaultFeeStructure }),
                    selectedPlan === 'CUSTOM' && plan.length > 0 && (React.createElement(PreviewCard, null,
                        React.createElement(PreviewHeader, null,
                            React.createElement(PreviewTitle, null, lang.paymentPlans.paymentPlan),
                            React.createElement(PreviewDateRange, null,
                                React.createElement("span", null, moment(plan[0]?.date).format(THEME.DATE_FORMAT_SHORT)),
                                React.createElement(ArrowRight, null),
                                React.createElement("span", null, moment(plan[plan.length - 1]?.date).format(THEME.DATE_FORMAT_SHORT)))),
                        React.createElement(PreviewStatsGrid, null,
                            React.createElement(PreviewStat, null,
                                React.createElement(PreviewStatLabel, null, lang.paymentPlans.entries),
                                React.createElement(PreviewStatValue, null, plan.length)),
                            React.createElement(PreviewStat, null,
                                React.createElement(PreviewStatLabel, null, lang.paymentPlans.amount),
                                React.createElement(PreviewStatValue, null,
                                    React.createElement(Money, { amount: paymentPlanHelpers.getAmount.entry(plan) || 0, currency: singleCase?.currency }),
                                    React.createElement(PreviewStatSuffix, null,
                                        "/",
                                        lang.paymentPlans.month))),
                            React.createElement(PreviewStat, { $accent: true },
                                React.createElement(PreviewStatLabel, null, lang.paymentPlans.totalPayment),
                                React.createElement(PreviewStatValue, null,
                                    React.createElement(Money, { amount: paymentPlanHelpers.getAmount.total(plan), currency: singleCase?.currency })))),
                        (paymentPlanHelpers.getAmount.upfront(plan) ||
                            paymentPlanHelpers.getAmount.rest(plan)) && (React.createElement(PreviewBreakdown, null,
                            !!paymentPlanHelpers.getAmount.upfront(plan) && (React.createElement(PreviewBreakdownRow, null,
                                React.createElement("span", null, lang.paymentPlans.upfrontPayment),
                                React.createElement("strong", null,
                                    React.createElement(Money, { amount: paymentPlanHelpers.getAmount.upfront(plan) || 0, currency: singleCase?.currency })))),
                            !!paymentPlanHelpers.getAmount.rest(plan) && (React.createElement(PreviewBreakdownRow, null,
                                React.createElement("span", null, lang.paymentPlans.rest),
                                React.createElement("strong", null,
                                    React.createElement(Money, { amount: paymentPlanHelpers.getAmount.rest(plan) || 0, currency: singleCase?.currency })))))),
                        React.createElement(ExclusiveContent, null, documents &&
                            economyPolicy &&
                            plan &&
                            fixedDistributions &&
                            conditions && (React.createElement(CalcDiff, { caseVouchers: props.caseVouchers, documents: documents, economyPolicy: economyPolicy, plan: plan, feeStructure: config?.feeStructure, fixedDistributions: fixedDistributions, conditions: conditions }))),
                        React.createElement(EntriesTableWrapper, null,
                            React.createElement(EntriesTable, null,
                                React.createElement("thead", null,
                                    React.createElement("tr", null,
                                        React.createElement("th", null, "#"),
                                        React.createElement("th", null, lang.paymentPlans.date),
                                        React.createElement("th", null, lang.paymentPlans.reminder),
                                        React.createElement("th", null, lang.paymentPlans.lastChanceOfPayment),
                                        React.createElement("th", null, lang.paymentPlans.cancelled),
                                        React.createElement("th", { style: { textAlign: 'right' } }, lang.paymentPlans.addedInterests),
                                        React.createElement("th", { style: { textAlign: 'right' } }, lang.paymentPlans.amount))),
                                React.createElement("tbody", null, plan.map((entry, i) => (React.createElement("tr", { key: moment(entry.date).format() +
                                        entry.amount +
                                        entry.type +
                                        entry.status },
                                    React.createElement("td", null,
                                        React.createElement(EntryNumber, null, i + 1)),
                                    React.createElement("td", null,
                                        React.createElement(EntryDate, null, moment(entry.date).format(THEME.DATE_FORMAT_SHORT))),
                                    React.createElement("td", null,
                                        React.createElement(Muted, null, moment(entry.reminder).format(THEME.DATE_FORMAT_SHORT))),
                                    React.createElement("td", null,
                                        React.createElement(Muted, null, moment(entry.deadline).format(THEME.DATE_FORMAT_SHORT))),
                                    React.createElement("td", null,
                                        React.createElement(Muted, null, moment(entry.cancel).format(THEME.DATE_FORMAT_SHORT))),
                                    React.createElement("td", { style: { textAlign: 'right' } },
                                        React.createElement(InterestAmount, null,
                                            "+",
                                            React.createElement(Money, { amount: paymentPlanHelpers.calculateInterestPerEntry(currentInterest, plan, i) }))),
                                    React.createElement("td", { style: { textAlign: 'right' } },
                                        React.createElement("strong", null,
                                            React.createElement(Money, { amount: entry.amount || 0 }))))))),
                                React.createElement("tfoot", null,
                                    React.createElement("tr", null,
                                        React.createElement("td", { colSpan: 5 }),
                                        React.createElement("td", { style: { textAlign: 'right' } },
                                            React.createElement(Money, { amount: plan[plan.length - 1]
                                                    ?.interestAmount != null
                                                    ? currentInterest -
                                                        plan[plan.length - 1]
                                                            .interestAmount
                                                    : 0 })),
                                        React.createElement("td", { style: { textAlign: 'right' } },
                                            React.createElement("strong", null,
                                                React.createElement(Money, { amount: paymentPlanHelpers.getAmount.total(plan) }))))))))))) : (singleCase && (React.createElement(PaymentPlanSuggestions, { caseVouchers: props.caseVouchers, singleCase: singleCase, standardPlans: paymentPlanConfig.plans, selectedPlan: selectedPlan, onSelectPlan: (id, p) => selectPlan(id, p, null) })))))))),
        !paymentPlan && (React.createElement("div", { style: {
                position: 'sticky',
                bottom: '0px',
                marginTop: '20px',
                background: 'white',
                padding: '20px 0px',
                borderTop: '1px solid lightgrey',
            } },
            React.createElement(Checkbox, { checked: activationTrigger, onChange: (e) => {
                    setActivationTrigger(e.target.checked);
                }, placeholder: lang.paymentPlans.confirmationCheckbox }),
            singleCase?.status === CaseStatus.ENDED ? (React.createElement(Errors, { errors: [lang.paymentPlans.errorCaseEnded] })) : null,
            React.createElement(Button, { loading: createPaymentPlanMutation.isLoading, disabled: !(selectedPlan && !paymentPlan) ||
                    singleCase?.status === CaseStatus.ENDED, onClick: () => createPaymentPlanMutation.mutate(), block: true, className: "ui-test-9fab98e3" }, lang.paymentPlans.createPaymentPlan))),
        React.createElement("div", null, paymentPlans &&
            singleCase &&
            paymentPlans
                .sort((a, b) => {
                if (moment(a.createdAt) > moment(b.createdAt)) {
                    return -1;
                }
                else if (moment(a.createdAt) < moment(b.createdAt)) {
                    return 1;
                }
                return 0;
            })
                .map((paymentPlan) => (React.createElement(PaymentPlanEntries, { key: paymentPlan.paymentPlanId, paymentPlan: paymentPlan },
                React.createElement(PaymentPlan, { paymentPlan: paymentPlan, tenantId: props.tenantId, caseVouchers: props.caseVouchers, singleCase: singleCase })))))))));
};
export default PaymentPlanCreator;
const PaymentPlanCreatorStyled = styled.div `
  .react-datepicker-wrapper {
    width: auto;
    display: block;
  }

  .react-datepicker__input-container {
    display: block;
  }
`;
const PreviewCard = styled.div `
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
  padding: 20px;
  background: #fff;
  border: 2px solid ${THEME.COLOR_ACCENT};
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
`;
const PreviewHeader = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`;
const PreviewTitle = styled.h4 `
  margin: 0;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #555;
  font-weight: 700;
`;
const PreviewDateRange = styled.div `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #666;
  font-size: 13px;
  font-variant-numeric: tabular-nums;

  svg {
    width: 12px;
    height: 12px;
    opacity: 0.6;
  }
`;
const PreviewStatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
`;
const PreviewStat = styled.div `
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  background: ${(props) => props.$accent ? `${THEME.COLOR_ACCENT}0d` : '#fafafa'};
  border: 1px solid
    ${(props) => (props.$accent ? `${THEME.COLOR_ACCENT}33` : '#eee')};
  border-radius: 8px;
`;
const PreviewStatLabel = styled.span `
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #888;
  font-weight: 600;
`;
const PreviewStatValue = styled.span `
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  font-size: 17px;
  font-weight: 700;
  color: #111;
  font-variant-numeric: tabular-nums;
  flex-wrap: wrap;
`;
const PreviewStatSuffix = styled.span `
  font-size: 12px;
  font-weight: 500;
  color: #888;
`;
const PreviewBreakdown = styled.div `
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 14px;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
`;
const PreviewBreakdownRow = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13px;
  color: #555;

  strong {
    color: #111;
    font-variant-numeric: tabular-nums;
  }
`;
const EntriesTableWrapper = styled.div `
  width: 100%;
  overflow-x: auto;
  border: 1px solid #eee;
  border-radius: 8px;
`;
const EntriesTable = styled.table `
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead th {
    padding: 10px 12px;
    text-align: left;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #888;
    font-weight: 600;
    background: #fafafa;
    border-bottom: 1px solid #eee;
    white-space: nowrap;
  }

  tbody td {
    padding: 10px 12px;
    border-bottom: 1px solid #f4f4f4;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: #fafafa;
  }

  tfoot td {
    padding: 12px;
    background: #fafafa;
    border-top: 2px solid #eee;
    font-variant-numeric: tabular-nums;
  }
`;
const EntryNumber = styled.span `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${THEME.COLOR_ACCENT}22;
  color: ${THEME.COLOR_ACCENT};
  font-size: 11px;
  font-weight: 700;
`;
const EntryDate = styled.span `
  font-weight: 600;
  color: #222;
`;
const Muted = styled.span `
  color: #888;
  font-size: 12px;
`;
const InterestAmount = styled.span `
  color: #b36b00;
  font-size: 12px;
`;
const CalcDiff = (props) => {
    // Add mock fee vouchers to account for fees in balance calculation
    const vouchersWithFees = useMemo(() => {
        if (!props.feeStructure) {
            return props.caseVouchers;
        }
        const feeVouchers = [];
        const now = new Date().toISOString();
        // Add initial fee
        if (props.feeStructure.initialFee &&
            props.feeStructure.initialFee.amount > 0) {
            feeVouchers.push(helpers.paymentPlans.mockCostCaseVoucher({
                amount: -props.feeStructure.initialFee.amount,
                voucherTypeId: props.feeStructure.initialFee.voucherTypeId,
                source: CaseVoucherSource.Collector,
                date: now,
            }));
        }
        // Add per-entry fees for each plan entry
        if (props.feeStructure.perEntryFee &&
            props.feeStructure.perEntryFee.amount > 0) {
            for (const entry of props.plan) {
                feeVouchers.push(helpers.paymentPlans.mockCostCaseVoucher({
                    amount: -props.feeStructure.perEntryFee.amount,
                    voucherTypeId: props.feeStructure.perEntryFee.voucherTypeId,
                    source: CaseVoucherSource.Collector,
                    date: entry.date,
                }));
            }
        }
        return [...props.caseVouchers, ...feeVouchers];
    }, [props.caseVouchers, props.feeStructure, props.plan]);
    const balanceLoader = useGetBalance(vouchersWithFees, props.documents, props.economyPolicy, props.fixedDistributions, props.plan, props.conditions);
    if (!h.hasData(balanceLoader) || balanceLoader.data === 0) {
        return null;
    }
    const balance = balanceLoader.data;
    return (React.createElement(React.Fragment, null,
        React.createElement("br", null),
        React.createElement(AlertCmp, { message: React.createElement(React.Fragment, null,
                "Amount has a diff. of ",
                React.createElement(Money, { amount: -balance })) })));
};
export const StyledPlan = styled.div `
  border-bottom: 1px solid lightgrey;
  transition: padding 0.3s;
  background-color: ${(props) => (props.active ? 'lightgrey' : 'transparent')};
  padding: ${(props) => props.active
    ? `${THEME.GRID_SPACING}px ${THEME.GRID_SPACING}px`
    : `${THEME.GRID_SPACING}px 0`};
  cursor: pointer;

  &:hover {
    padding: ${THEME.GRID_SPACING}px ${THEME.GRID_SPACING}px;
    background-color: ${(props) => (props.active ? 'lightgrey' : '#efefef')};
  }

  &:last-child {
    border-bottom: none;
  }

  & > h2 {
    margin: 0 !important;
  }

  p {
    font-weight: bold;
    color: ${THEME.DARK_COLOR_SECONDARY};
    margin-top: 8px;
  }

  p:last-child {
    font-weight: initial;
    color: inherit;
    margin-bottom: 0px;
  }

  div.price {
    font-size: 3em;
    float: right;
    text-align: right;

    & > span > div {
      font-size: 0.35em;

      &:last-child,
      &.pp-details {
        font-size: 0.5em;

        & > span:first-child {
          font-size: 0.5em;
        }
      }
    }
  }
`;
//# sourceMappingURL=PaymentPlanCreator.js.map